import { SIDEBAR_VIEW_STORAGE_KEY } from "@frontend-shared/constants";
import type { ESidebarView } from "@frontend-shared/types";
import { DEFAULT_SIDEBAR_VIEW, isSidebarView } from "@frontend-shared/types";
import { useCallback, useState } from "react";

import { localStorageAdapter } from "@/lib/storage-adapter";

interface IUseSidebarViewResult {
  setView: (view: ESidebarView) => void;
  view: ESidebarView;
}

// NOTE: NSAI-6342 — which of the three sidebar tabs is open, remembered across
// reloads under the SAME storage key mobile writes (NSAI-5622), so the two
// clients agree on what "the view I left it on" means.
//
// NOTE: The stored value is whatever a previous visit — or an older build's
// spelling — left behind, so it is validated rather than trusted. Unlike
// mobile's AsyncStorage there is no hydration step to wait for: localStorage
// answers synchronously, so the first render already shows the right tab and
// no re-sync effect is needed.
const readStoredView = (): ESidebarView => {
  const storedView = localStorageAdapter.getItem(SIDEBAR_VIEW_STORAGE_KEY);

  return isSidebarView(storedView) ? storedView : DEFAULT_SIDEBAR_VIEW;
};

export const useSidebarView = (): IUseSidebarViewResult => {
  const [view, setViewState] = useState<ESidebarView>(readStoredView);

  const setView = useCallback((nextView: ESidebarView): void => {
    setViewState(nextView);
    localStorageAdapter.setItem(SIDEBAR_VIEW_STORAGE_KEY, nextView);
  }, []);

  return { setView, view };
};
